import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type LegalDocumentType = 'terms-of-service' | 'privacy-policy' | 'po-terms' | 'po-terms-summary';

export interface LegalDocument {
  id: string;
  document_type: LegalDocumentType;
  version: string;
  title: string;
  content: string;
  effective_date: string | null;
  is_active: boolean;
  activated_at: string | null;
  inactivated_at: string | null;
  created_at: string;
  created_by_user_id: string | null;
  updated_at: string;
}

/**
 * Fetch the active document for a specific type (for public pages)
 */
export function useActiveLegalDocument(documentType: LegalDocumentType) {
  return useQuery({
    queryKey: ['legal-document', 'active', documentType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('document_type', documentType)
        .eq('is_active', true)
        .single();

      if (error) {
        // No active document found is not an error for the UI
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data as LegalDocument;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch all versions of a document type (for admin page)
 */
export function useLegalDocumentVersions(documentType: LegalDocumentType) {
  return useQuery({
    queryKey: ['legal-documents', 'versions', documentType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('document_type', documentType)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as LegalDocument[];
    },
  });
}

/**
 * Fetch a specific document by ID (for viewing/editing)
 */
export function useLegalDocumentById(documentId: string | null) {
  return useQuery({
    queryKey: ['legal-document', documentId],
    queryFn: async () => {
      if (!documentId) return null;
      
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('id', documentId)
        .single();

      if (error) throw error;
      return data as LegalDocument;
    },
    enabled: !!documentId,
  });
}

interface CreateDocumentInput {
  document_type: LegalDocumentType;
  version: string;
  title: string;
  content: string;
  effective_date: string | null;
}

/**
 * Create a new document version
 */
export function useCreateLegalDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateDocumentInput) => {
      const { data: userData } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('legal_documents')
        .insert({
          ...input,
          created_by_user_id: userData.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as LegalDocument;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', 'versions', data.document_type] });
      toast.success('Document version created');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create document: ${error.message}`);
    },
  });
}

/**
 * Update a document (only drafts should be editable)
 */
export function useUpdateLegalDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<LegalDocument> & { id: string }) => {
      const { data, error } = await supabase
        .from('legal_documents')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as LegalDocument;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', 'versions', data.document_type] });
      queryClient.invalidateQueries({ queryKey: ['legal-document', data.id] });
      toast.success('Document updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update document: ${error.message}`);
    },
  });
}

/**
 * Activate a document (deactivates the previously active one)
 */
export function useActivateLegalDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, documentType }: { documentId: string; documentType: LegalDocumentType }) => {
      const { error } = await supabase.rpc('activate_legal_document', {
        document_id: documentId,
      });

      if (error) throw error;
      return { documentId, documentType };
    },
    onSuccess: ({ documentType }) => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', 'versions', documentType] });
      queryClient.invalidateQueries({ queryKey: ['legal-document', 'active', documentType] });
      toast.success('Document activated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to activate document: ${error.message}`);
    },
  });
}

/**
 * Delete a document (should only be used for drafts)
 */
export function useDeleteLegalDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, documentType }: { documentId: string; documentType: LegalDocumentType }) => {
      const { error } = await supabase
        .from('legal_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
      return { documentType };
    },
    onSuccess: ({ documentType }) => {
      queryClient.invalidateQueries({ queryKey: ['legal-documents', 'versions', documentType] });
      toast.success('Document deleted');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete document: ${error.message}`);
    },
  });
}

/**
 * Document type display names and metadata
 */
export const DOCUMENT_TYPE_CONFIG: Record<LegalDocumentType, { label: string; defaultTitle: string }> = {
  'terms-of-service': {
    label: 'Terms of Service',
    defaultTitle: 'Platform Terms of Service',
  },
  'privacy-policy': {
    label: 'Privacy Policy',
    defaultTitle: 'Privacy Policy',
  },
  'po-terms': {
    label: 'PO Terms',
    defaultTitle: 'Purchase Order Terms and Conditions',
  },
  'po-terms-summary': {
    label: 'PO Terms Summary',
    defaultTitle: 'Purchase Order Terms Summary',
  },
};
